import * as React from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FamilySummaryFieldResponse } from "@/types/contract-family";

interface FormValues {
  extraction_field_id: string;
  prompts: Record<string, string>;
}

interface Props {
  fields: FamilySummaryFieldResponse[];
  prompts: Record<string, string>;
  isLoading: boolean;
  onCancel: () => void;
  onRerun: (fieldId: string, prompt: string) => Promise<void>;
}

export const CompletedSummaryPromptEditor = ({ fields, prompts, isLoading, onCancel, onRerun }: Props) => {
  const legendId = React.useId();
  const { register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { extraction_field_id: fields[0]?.extraction_field_id ?? "", prompts },
  });
  const selectedFieldId = watch("extraction_field_id");
  const promptValues = watch("prompts");

  return (
    <form
      className="space-y-4"
      onSubmit={handleSubmit(values =>
        onRerun(values.extraction_field_id, values.prompts[values.extraction_field_id]?.trim() ?? ""),
      )}
    >
      <div className="bg-desert-sand-50 rounded-md border border-desert-sand-300 p-3 text-sm text-slate-700">
        Existing values remain unchanged until the replacement is generated and reviewed.
      </div>
      <p className="text-sm text-slate-600">Leave a field blank to use the value from the MASTER document.</p>
      <fieldset className="space-y-3" aria-labelledby={legendId}>
        <legend id={legendId} className="text-sm font-semibold text-slate-950">
          Choose one column to rerun
        </legend>
        {fields.map(field => {
          const selected = field.extraction_field_id === selectedFieldId;
          return (
            <div key={field.extraction_field_id} className="rounded-md border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <input
                  type="radio"
                  value={field.extraction_field_id}
                  {...register("extraction_field_id", { required: true })}
                />
                {field.name}
              </label>
              <Textarea
                className="mt-2 min-h-20 bg-white"
                aria-label={`Summary prompt for ${field.name}`}
                readOnly={!selected}
                {...register(`prompts.${field.extraction_field_id}`)}
              />
              {selected && (promptValues?.[field.extraction_field_id] ?? "").trim().length === 0 ? (
                <span className="mt-1 block text-xs text-slate-500">Using MASTER value</span>
              ) : null}
            </div>
          );
        })}
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={isLoading} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!selectedFieldId || isLoading}>
          {isLoading ? "Starting…" : "Rerun column"}
        </Button>
      </div>
    </form>
  );
};
